import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, ExternalLink, Linkedin, Mail, MapPin, Phone, Award, GraduationCap, BookOpen, FileText, Image, Video, File, Globe, Download, Save, Loader2, Lightbulb, ListChecks, Plus, CheckCircle, RefreshCw, TrendingUp, AlertCircle, X, Eye, FolderOpen, ClipboardList, Users, Bookmark, Heart, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';
import SkillHighlightTabs from '../components/SkillHighlightTabs';
import CredlyBadges from '../components/CredlyBadges';
import GoogleSitesIntegration from '../components/GoogleSitesIntegration';
import PortfolioUpload from '../components/PortfolioUpload';
import platformAnalytics from '../services/platformAnalytics';
import { scorePortfolio } from '../services/aiService';

const PortfolioPage = () => {
  const { isAuthenticated } = useAuth();
  const [primaryResume, setPrimaryResume] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [highlightedSkill, setHighlightedSkill] = useState(null);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null); // Ref-based debounce survives unmount
  const [googleSiteUrl, setGoogleSiteUrl] = useState('');
  const [googleSiteSaveStatus, setGoogleSiteSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | null

  // Portfolio Score state (Feature D)
  const [portfolioScore, setPortfolioScore] = useState(null); // { score, strengths, suggestions }
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringError, setScoringError] = useState(null);

  // AI Suggested Skills state (Feature C)
  const [allExtractedSkills, setAllExtractedSkills] = useState([]); // aggregated from all files
  const [addedSkills, setAddedSkills] = useState(new Set()); // skills already added to resume

  // Generated Bullets state (Feature A)
  const [allGeneratedBullets, setAllGeneratedBullets] = useState({}); // { [fileId]: [bullet1, bullet2] }
  const [showBulletsModal, setShowBulletsModal] = useState(false);
  const [addingBullets, setAddingBullets] = useState(false);
  const [selectedBullets, setSelectedBullets] = useState(new Set());
  const [bulletsSaved, setBulletsSaved] = useState(false);

  useEffect(() => {
    loadPrimaryResume();
  }, []);

  useEffect(() => {
    platformAnalytics.startPageTracking('page_view_portfolio', '/portfolio');
    return () => {
      platformAnalytics.stopPageTracking();
    };
  }, []);

  // Save portfolio files to the Users table (profile), NOT to any resume.
  const handleFilesAdded = useCallback((files) => {
    setPortfolioFiles(files);

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving');
      try {
        const cleanFiles = files.map(({ file, ...rest }) => rest);
        await resumeAPI.updateMyProfile({ portfolio_files: cleanFiles });
        setAutoSaveStatus('saved');
        console.log('[Save] Portfolio files saved to profile:', cleanFiles.length, 'files');
        setTimeout(() => setAutoSaveStatus(null), 2000);
      } catch (err) {
        console.error('[Save] Failed:', err);
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus(null), 3000);
      }
    }, 1000);
  }, []);

  const loadPrimaryResume = async () => {
    setLoading(true);
    try {
      // Load resume data (for resume-specific fields like experience, skills, etc.)
      const data = await resumeAPI.fetchPrimary();
      if (data && data.length > 0) {
        setPrimaryResume(data[0]);
      }

      // Load portfolio data from user profile (separate from resumes)
      const profile = await resumeAPI.getMyProfile();
      if (profile) {
        setProfileData(profile);
        setPortfolioFiles(profile.portfolio_files || []);
        const loadedUrl = profile.google_site_url || '';
        console.log('[PortfolioPage] google_site_url from profile:', loadedUrl || '(empty)');
        setGoogleSiteUrl(loadedUrl);
        if (profile.portfolio_score) {
          setPortfolioScore(profile.portfolio_score);
        }
      }
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePortfolio = async () => {
    setSaving(true);
    try {
      const cleanFiles = portfolioFiles.map(({ file, ...rest }) => rest);
      await resumeAPI.updateMyProfile({
        portfolio_files: cleanFiles,
        google_site_url: googleSiteUrl,
        portfolio_score: portfolioScore
      });
      setPortfolioFiles(cleanFiles);
      alert('Portfolio saved successfully!');
    } catch (error) {
      console.error('Failed to save portfolio:', error);
      alert('Failed to save portfolio. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGoogleSiteUrlUpdate = async (url) => {
    setGoogleSiteUrl(url);
    setGoogleSiteSaveStatus('saving');

    try {
      await resumeAPI.updateMyProfile({ google_site_url: url });
      setGoogleSiteSaveStatus('saved');
      console.log('[GoogleSites] URL saved to profile:', url || '(cleared)');

      platformAnalytics.trackEvent('google_sites_integrated', '/portfolio', { site_url: url });
      setTimeout(() => setGoogleSiteSaveStatus(null), 3000);
    } catch (error) {
      console.error('[GoogleSites] Failed to save URL:', error);
      setGoogleSiteSaveStatus('error');
      setTimeout(() => setGoogleSiteSaveStatus(null), 5000);
    }
  };

  // ─────────────── Feature D: Portfolio Scoring ───────────────

  const handleScorePortfolio = async () => {
    if (!primaryResume) return;
    setScoringLoading(true);
    setScoringError(null);

    try {
      const result = await scorePortfolio({
        files: portfolioFiles,
        resume: primaryResume
      });
      setPortfolioScore(result);

      // Persist score to profile (separate from resumes)
      try {
        await resumeAPI.updateMyProfile({ portfolio_score: result });
      } catch (err) {
        console.error('Failed to persist portfolio score:', err);
      }
    } catch (err) {
      console.error('[Portfolio Score]', err);
      setScoringError(err.message);
    } finally {
      setScoringLoading(false);
    }
  };

  // ─────────────── Feature C: Skills from PortfolioUpload ───────────────

  const handleSkillsExtracted = (fileId, skills) => {
    setAllExtractedSkills(prev => {
      const existing = prev.filter(s => s.fileId !== fileId);
      return [...existing, ...skills.map(s => ({ fileId, skill: s }))];
    });
  };

  const handleAddSkillToResume = async (skillName) => {
    if (!primaryResume) return;
    const resumeId = primaryResume.id || primaryResume._id;
    if (!resumeId) return;

    try {
      // Fetch fresh skills list to check for duplicates, then send ONLY skills field
      const freshResume = await resumeAPI.fetchById(resumeId);
      const currentSkills = freshResume.skills || [];
      if (currentSkills.map(s => s.toLowerCase()).includes(skillName.toLowerCase())) {
        setAddedSkills(prev => new Set(prev).add(skillName));
        return;
      }

      const updatedSkills = [...currentSkills, skillName];
      // Send only the skills field — backend merges with existing resume_json
      await resumeAPI.update(resumeId, { skills: updatedSkills });
      setPrimaryResume(prev => ({ ...prev, skills: updatedSkills }));
      setAddedSkills(prev => new Set(prev).add(skillName));
    } catch (err) {
      console.error('[Add Skill]', err);
      alert('Failed to add skill: ' + err.message);
    }
  };

  // ─────────────── Feature A: Bullets from PortfolioUpload ───────────────

  const handleBulletsGenerated = (fileId, bullets) => {
    setAllGeneratedBullets(prev => ({ ...prev, [fileId]: bullets }));
  };

  const toggleBulletSelection = (key) => {
    setSelectedBullets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAddBulletsToResume = async () => {
    if (!primaryResume || selectedBullets.size === 0) return;
    const resumeId = primaryResume.id || primaryResume._id;
    if (!resumeId) return;

    setAddingBullets(true);
    try {
      // Collect selected bullet texts
      const bulletTexts = [];
      for (const key of selectedBullets) {
        const [fileId, idx] = key.split('::');
        const bullets = allGeneratedBullets[fileId];
        if (bullets && bullets[parseInt(idx, 10)]) {
          bulletTexts.push(bullets[parseInt(idx, 10)]);
        }
      }

      if (bulletTexts.length === 0) return;

      // Fetch fresh experience to append new entry, then send ONLY experience field
      const freshResume = await resumeAPI.fetchById(resumeId);
      const experience = freshResume.experience || [];
      const newEntry = {
        title: 'Portfolio Highlights',
        company: '',
        location: '',
        start_date: '',
        end_date: '',
        current: false,
        description: bulletTexts.map(b => `• ${b}`).join('\n')
      };

      const updatedExperience = [...experience, newEntry];
      // Send only the experience field — backend merges with existing resume_json
      await resumeAPI.update(resumeId, { experience: updatedExperience });
      setPrimaryResume(prev => ({ ...prev, experience: updatedExperience }));
      setBulletsSaved(true);
      setTimeout(() => {
        setShowBulletsModal(false);
        setBulletsSaved(false);
        setSelectedBullets(new Set());
      }, 1500);
    } catch (err) {
      console.error('[Add Bullets]', err);
      alert('Failed to add bullets to resume: ' + err.message);
    } finally {
      setAddingBullets(false);
    }
  };

  // ─────────────── Render helpers ───────────────

  const allBulletsFlat = Object.entries(allGeneratedBullets).flatMap(([fileId, bullets]) =>
    bullets.map((bullet, idx) => ({
      key: `${fileId}::${idx}`,
      text: bullet,
      fileName: portfolioFiles.find(f => String(f.id) === String(fileId))?.name || 'Document'
    }))
  );

  // Aggregate unique skills across all files, de-duped against resume skills
  const existingSkillsLower = [
    ...(primaryResume?.skills || []),
    ...(primaryResume?.core_skills || []).map(s => typeof s === 'string' ? s : s.name)
  ].map(s => s.toLowerCase());

  const uniqueExtractedSkills = allExtractedSkills
    .map(s => s.skill)
    .filter((skill, idx, arr) => arr.indexOf(skill) === idx)
    .filter(skill => !existingSkillsLower.includes(skill.toLowerCase()))
    .filter(skill => !addedSkills.has(skill));

  // ─────────────── Loading / Empty states ───────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (!primaryResume) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">No Primary Resume Set</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Set one of your resumes as primary to display it as your portfolio
          </p>
          <Link to="/my-resumes" className="btn btn-primary">
            Go to My Resumes
          </Link>
        </div>
      </div>
    );
  }

  const highlightColor = primaryResume.highlight_color || '#3b82f6';
  const coreSkillsRaw = primaryResume.core_skills;
  const coreSkills =
    Array.isArray(coreSkillsRaw) && coreSkillsRaw.length > 0
      ? coreSkillsRaw
      : (Array.isArray(primaryResume.skills) ? primaryResume.skills : []);
  const credlyBadges = primaryResume.credly_badges || [];

  const filesByCategory = portfolioFiles.reduce((acc, file) => {
    if (!acc[file.category]) acc[file.category] = [];
    acc[file.category].push(file);
    return acc;
  }, {});

  const categoryIcons = {
    word: FileText, excel: FileText, powerpoint: FileText,
    images: Image, videos: Video, awards: Award,
    certificates: GraduationCap, projects: FileText,
    leadership: Users, case_studies: Bookmark,
    community_service: Heart, performance_evaluations: Star,
    publications: FileText, training: BookOpen,
    reports: FileText, other: File
  };

  const categoryLabels = {
    word: 'Word Documents', excel: 'Excel Spreadsheets',
    powerpoint: 'PowerPoint Presentations', images: 'Pictures/Images',
    videos: 'Videos', awards: 'Awards', certificates: 'Certificates',
    projects: 'Projects',
    leadership: 'Leadership & Team Development', case_studies: 'Case Studies',
    community_service: 'Community & Service', performance_evaluations: 'Performance Evaluations',
    publications: 'Publications', training: 'Training Materials',
    reports: 'Reports & Analysis', other: 'Other'
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Score color based on value
  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#eab308';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="min-h-screen bg-gray-50" id="resume-content">
      {/* Hero Section with Banner and Floating Picture */}
      <section
        className="relative h-96 bg-gradient-to-br from-primary-600 to-primary-800"
        style={
          primaryResume.banner_url
            ? {
                backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(29, 78, 216, 0.9)), url(${primaryResume.banner_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : {}
        }
      >
        <div className="container-custom h-full flex items-end pb-8 relative">
          {primaryResume.photo_url && (
            <div className="absolute -bottom-20 left-8 z-20">
              <div className="relative">
                <div 
                  className="absolute inset-0 rounded-full p-1 shadow-2xl"
                  style={{ backgroundColor: highlightColor }}
                ></div>
                <img
                  src={primaryResume.photo_url}
                  alt={primaryResume.title}
                  className="w-40 h-40 rounded-full object-cover border-4 border-white relative z-10"
                />
              </div>
            </div>
          )}
          
          <div className={`flex items-end gap-6 w-full ${primaryResume.photo_url ? 'ml-56' : ''}`}>
            <div className="flex-1 text-white pb-2">
              <h1 className="text-5xl font-bold mb-4">
                {(primaryResume.title || '').trim() ||
                  (primaryResume.target_role || '').trim() ||
                  'My Portfolio'}
              </h1>
              {(primaryResume.headline || '').trim() && (
                <p className="text-2xl text-primary-100 mb-2">{primaryResume.headline}</p>
              )}
              {(primaryResume.target_role || '').trim() && (
                <p className="text-lg text-primary-50">{primaryResume.target_role}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Bar */}
      <section className="bg-white border-b border-gray-200 mt-20">
        <div className="container-custom py-6">
          <div className="flex flex-wrap gap-6 items-center justify-center">
            {primaryResume.contact_email && (
              <a
                href={`mailto:${primaryResume.contact_email}`}
                className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{primaryResume.contact_email}</span>
              </a>
            )}
            {primaryResume.contact_phone && (
              <a
                href={`tel:${primaryResume.contact_phone}`}
                className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{primaryResume.contact_phone}</span>
              </a>
            )}
            {primaryResume.location && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-5 h-5" />
                <span>{primaryResume.location}</span>
              </div>
            )}
            {primaryResume.linkedin_url && (
              <a
                href={primaryResume.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
                <span>LinkedIn</span>
              </a>
            )}
            {primaryResume.website_url && (
              <a
                href={primaryResume.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                <span>Website</span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Navigation Buttons Bar */}
      <section className="bg-white border-b border-gray-200">
        <div className="container-custom py-4">
          <div className="flex flex-wrap gap-3 items-center justify-center">
            {/* Portfolio Files button */}
            <Link
              to="/portfolio/files"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
            >
              <FolderOpen className="w-4 h-4" />
              Portfolio Files
            </Link>

            {/* Portfolio (Public View) button */}
            {(profileData?.public_link_id || primaryResume?.public_link_id) && (
              <Link
                to={`/portfolio/public/${profileData?.public_link_id || primaryResume?.public_link_id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm"
              >
                <Globe className="w-4 h-4" />
                Portfolio (Public View)
              </Link>
            )}

            {/* Google Sites button - only if connected */}
            {googleSiteUrl && (
              <a
                href={googleSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
              >
                <Globe className="w-4 h-4" />
                Google Sites
              </a>
            )}
            {!googleSiteUrl && isAuthenticated && (
              <span
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed"
                title="Connect your Google Sites account first"
              >
                <Globe className="w-4 h-4" />
                Google Sites (Not Connected)
              </span>
            )}

            {/* Portfolio Improvement Survey - Coming Soon */}
            <span
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-gray-200 text-gray-400 cursor-not-allowed"
              title="Coming Soon"
            >
              <ClipboardList className="w-4 h-4" />
              Portfolio Improvement Survey
              <span className="text-xs bg-gray-300 text-gray-500 px-1.5 py-0.5 rounded">Soon</span>
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-custom py-12">
        {/* ═══════ Portfolio Score Card (Feature D) ═══════ */}
        {isAuthenticated && (
          <div className="mb-8">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6" style={{ color: highlightColor }} />
                  Portfolio Score
                </h2>
                <button
                  onClick={handleScorePortfolio}
                  disabled={scoringLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {scoringLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Scoring...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> {portfolioScore ? 'Re-score' : 'Score My Portfolio'}</>
                  )}
                </button>
              </div>

              {scoringError && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{scoringError}</p>
                </div>
              )}

              {portfolioScore ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Score Gauge */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-36 h-36">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="52" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                        <circle
                          cx="60" cy="60" r="52" fill="none"
                          stroke={getScoreColor(portfolioScore.score)}
                          strokeWidth="10"
                          strokeLinecap="round"
                          strokeDasharray={`${(portfolioScore.score / 100) * 327} 327`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-3xl font-bold" style={{ color: getScoreColor(portfolioScore.score) }}>
                            {portfolioScore.score}
                          </span>
                          <span className="text-sm text-gray-500 block">/100</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{portfolioFiles.length} file{portfolioFiles.length !== 1 ? 's' : ''} in portfolio</p>
                  </div>

                  {/* Strengths */}
                  <div>
                    <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" /> Strengths
                    </h3>
                    {portfolioScore.strengths && portfolioScore.strengths.length > 0 ? (
                      <ul className="space-y-2">
                        {portfolioScore.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Score your portfolio to see strengths</p>
                    )}
                  </div>

                  {/* Suggestions */}
                  <div>
                    <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-1.5">
                      <Lightbulb className="w-4 h-4" /> Suggestions
                    </h3>
                    {portfolioScore.suggestions && portfolioScore.suggestions.length > 0 ? (
                      <ul className="space-y-2">
                        {portfolioScore.suggestions.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Score your portfolio to see suggestions</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Click "Score My Portfolio" to get an AI-powered evaluation of your portfolio's strength and personalized improvement suggestions.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════ AI Suggested Skills (Feature C) ═══════ */}
        {isAuthenticated && uniqueExtractedSkills.length > 0 && (
          <div className="mb-8">
            <div className="card border-l-4 border-purple-500">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-purple-600" />
                AI Suggested Skills
                <span className="text-sm font-normal text-gray-500">({uniqueExtractedSkills.length} new)</span>
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Skills extracted from your portfolio documents. Click "Add" to add them to your resume.
              </p>
              <div className="flex flex-wrap gap-2">
                {uniqueExtractedSkills.map((skill, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAddSkillToResume(skill)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 transition-colors"
                    title={`Add "${skill}" to your resume`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {skill}
                  </button>
                ))}
              </div>
              {addedSkills.size > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Array.from(addedSkills).map((skill, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
                      <CheckCircle className="w-3.5 h-3.5" />
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════ Generated Bullets Quick Access (Feature A) ═══════ */}
        {isAuthenticated && allBulletsFlat.length > 0 && (
          <div className="mb-8">
            <div className="card border-l-4 border-purple-500">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-purple-600" />
                  AI Generated Resume Bullets
                  <span className="text-sm font-normal text-gray-500">({allBulletsFlat.length})</span>
                </h2>
                <button
                  onClick={() => setShowBulletsModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add to Resume
                </button>
              </div>
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {allBulletsFlat.slice(0, 6).map((b) => (
                  <li key={b.key} className="flex items-start gap-2 text-sm">
                    <span className="text-purple-400 mt-0.5">•</span>
                    <span className="text-gray-700">{b.text}</span>
                  </li>
                ))}
                {allBulletsFlat.length > 6 && (
                  <li className="text-sm text-purple-600 cursor-pointer hover:underline" onClick={() => setShowBulletsModal(true)}>
                    ... and {allBulletsFlat.length - 6} more. Click to view all.
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Credly Badges */}
        {credlyBadges.length > 0 && (
          <div className="card mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: highlightColor }} />
              Certifications & Badges
            </h2>
            <CredlyBadges badges={credlyBadges} onBadgesUpdate={() => {}} />
          </div>
        )}

        {/* Two-column layout: Portfolio Files (left) | Google Sites + Example (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
              {/* ── LEFT COLUMN: Portfolio Files ── */}
              <div>
                {/* Portfolio Files - Upload & Manage (authenticated) */}
                {isAuthenticated && (
                  <div className="card mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <File className="w-6 h-6" style={{ color: highlightColor }} />
                        My Portfolio Files
                        {portfolioFiles.length > 0 && (
                          <span className="text-base font-normal text-gray-500">({portfolioFiles.length})</span>
                        )}
                      </h2>
                      <div className="flex items-center gap-3">
                        {autoSaveStatus === 'saving' && (
                          <span className="text-sm text-blue-600 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Auto-saving...</span>
                        )}
                        {autoSaveStatus === 'saved' && (
                          <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Saved</span>
                        )}
                        {autoSaveStatus === 'error' && (
                          <span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Auto-save failed</span>
                        )}
                        <button
                          onClick={handleSavePortfolio}
                          disabled={saving}
                          className="btn btn-primary inline-flex items-center gap-2"
                        >
                          <Save className="w-5 h-5" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                    <PortfolioUpload
                      onFilesAdded={handleFilesAdded}
                      existingPortfolio={portfolioFiles}
                      onSkillsExtracted={handleSkillsExtracted}
                      onBulletsGenerated={handleBulletsGenerated}
                      hideExistingFiles={true}
                    />
                    {portfolioFiles.length > 0 && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                        <FolderOpen className="w-4 h-4 flex-shrink-0" />
                        <span>
                          You have {portfolioFiles.length} saved file{portfolioFiles.length !== 1 ? 's' : ''}.{' '}
                          <Link to="/portfolio/files" className="font-semibold underline hover:text-blue-900">
                            View & manage on the Portfolio Files page
                          </Link>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Portfolio Files - View Mode (non-authenticated visitors) */}
                {!isAuthenticated && Object.keys(filesByCategory).length > 0 && (
                  <div className="card mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <File className="w-6 h-6" style={{ color: highlightColor }} />
                        Portfolio Files ({portfolioFiles.length})
                      </h2>
                    </div>
                    <div className="space-y-6">
                      {Object.entries(filesByCategory).map(([categoryId, files]) => {
                        const IconComponent = categoryIcons[categoryId] || File;
                        return (
                          <div key={categoryId} className="border-l-4 pl-4" style={{ borderColor: highlightColor }}>
                            <div className="flex items-center gap-2 mb-3">
                              <IconComponent className="w-5 h-5" style={{ color: highlightColor }} />
                              <h3 className="text-lg font-semibold text-gray-900">
                                {categoryLabels[categoryId] || categoryId}
                              </h3>
                              <span className="text-sm text-gray-500">({files.length})</span>
                            </div>
                            <div className="space-y-3">
                              {files.map((fileObj) => (
                                <div
                                  key={fileObj.id}
                                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3 flex-1">
                                      <div className="mt-1">
                                        <IconComponent className="w-5 h-5 text-gray-400" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="font-medium text-gray-900">{fileObj.name}</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                          {formatFileSize(fileObj.size)} • Uploaded {new Date(fileObj.uploadedAt).toLocaleDateString()}
                                        </p>
                                        {fileObj.summary && (
                                          <div className="mt-2">
                                            <p className="text-sm text-gray-700 leading-relaxed">{fileObj.summary}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {fileObj.url && (
                                      <a
                                        href={fileObj.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-4 p-2 rounded-lg hover:bg-white transition-colors"
                                        style={{ color: highlightColor }}
                                        title="Download file"
                                      >
                                        <Download className="w-5 h-5" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ── RIGHT COLUMN: Google Sites + Example Portfolio ── */}
              <div>
                {/* Google Sites Integration */}
                {isAuthenticated && (
                  <div className="card mb-6">
                    <GoogleSitesIntegration 
                      googleSiteUrl={googleSiteUrl}
                      onUrlUpdate={handleGoogleSiteUrlUpdate}
                      saveStatus={googleSiteSaveStatus}
                    />
                  </div>
                )}
                {!isAuthenticated && googleSiteUrl && (
                  <div className="card mb-6">
                    <GoogleSitesIntegration 
                      googleSiteUrl={googleSiteUrl}
                      onUrlUpdate={() => {}}
                    />
                  </div>
                )}

                {/* View Example Portfolio Button */}
                <div className="card mb-6 text-center">
                  <div className="py-4">
                    <p className="text-gray-600 mb-4">
                      Want to see what a fully built portfolio looks like? Check out our example showcase.
                    </p>
                    <Link
                      to="/portfolio/example"
                      className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                      View Example Portfolio
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

      {/* ═══════ Add Bullets to Resume Modal (Feature A) ═══════ */}
      {showBulletsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-purple-600" />
                Add Bullets to Resume
              </h2>
              <button onClick={() => setShowBulletsModal(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-gray-500 mb-4">
                Select the bullet points you want to add to your resume as a new "Portfolio Highlights" experience entry.
              </p>
              <ul className="space-y-3">
                {allBulletsFlat.map((b) => (
                  <li
                    key={b.key}
                    onClick={() => toggleBulletSelection(b.key)}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedBullets.has(b.key)
                        ? 'border-purple-300 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedBullets.has(b.key)
                        ? 'border-purple-600 bg-purple-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedBullets.has(b.key) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-800">{b.text}</p>
                      <p className="text-xs text-gray-400 mt-1">From: {b.fileName}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {selectedBullets.size} bullet{selectedBullets.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowBulletsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddBulletsToResume}
                  disabled={selectedBullets.size === 0 || addingBullets || bulletsSaved}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {bulletsSaved ? (
                    <><CheckCircle className="w-4 h-4" /> Saved!</>
                  ) : addingBullets ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Add to Resume</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
